import React, { useState, useEffect } from 'react';
import { Users, Monitor, FileText, AlertCircle, ShieldCheck } from 'lucide-react';
import { registryApi } from '../../services/registryService';
import TierBadge from '../../components/registry/TierBadge';

const GAP_TABS = [
  { key: 'ownership', label: 'Ownership', icon: Users },
  { key: 'dashboards', label: 'Dashboards', icon: Monitor },
  { key: 'log_groups', label: 'Log Groups', icon: FileText },
  { key: 'alarms', label: 'Alarms', icon: AlertCircle },
];

function SummaryCard({ icon: Icon, label, count, total, color }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  const barColor = pct >= 66 ? 'bg-emerald-500' : pct >= 33 ? 'bg-amber-500' : 'bg-red-500';

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="text-xs text-gray-500">{label}</p>
          <p className="text-lg font-bold text-gray-900">{count} / {total}</p>
        </div>
      </div>
      <div className="w-full bg-gray-100 rounded-full h-2">
        <div className={`${barColor} h-2 rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <p className="text-xs text-gray-500 mt-1">{pct}% coverage</p>
    </div>
  );
}

export default function GapDashboard({ onNavigate }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('ownership');

  useEffect(() => {
    const fetchGaps = async () => {
      try {
        const result = await registryApi.getGaps();
        setReport(result.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchGaps();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return <div className="bg-red-50 text-red-700 p-4 rounded-lg">{error}</div>;
  }

  if (!report) return null;

  const { summary, gaps } = report;
  const activeGaps = gaps[activeTab] || [];

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900">Gap Dashboard</h2>
        <p className="text-sm text-gray-500 mt-1">
          Platform observability and ownership coverage for {summary.total_services} services
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <SummaryCard
          icon={Users}
          label="Ownership"
          count={summary.ownership_assigned}
          total={summary.total_services}
          color="bg-purple-500"
        />
        <SummaryCard
          icon={Monitor}
          label="Dashboards"
          count={summary.dashboards_present}
          total={summary.total_services}
          color="bg-blue-500"
        />
        <SummaryCard
          icon={FileText}
          label="Log Groups"
          count={summary.log_groups_present}
          total={summary.total_services}
          color="bg-cyan-500"
        />
        <SummaryCard
          icon={AlertCircle}
          label="Alarms"
          count={summary.alarms_present}
          total={summary.total_services}
          color="bg-orange-500"
        />
        <SummaryCard
          icon={ShieldCheck}
          label="Overall Health"
          count={summary.overall_health_pct}
          total={100}
          color="bg-emerald-500"
        />
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 mb-4 border-b border-gray-200">
        {GAP_TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === key
                ? 'border-amber-500 text-amber-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
            <span className={`text-xs px-1.5 py-0.5 rounded-full ${
              activeTab === key ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'
            }`}>
              {(gaps[key] || []).length}
            </span>
          </button>
        ))}
      </div>

      {/* Gap table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-4 py-3 text-left font-medium text-gray-600">Tier</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">Service</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">Gap</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {activeGaps.length === 0 ? (
              <tr>
                <td colSpan="3" className="px-4 py-8 text-center text-gray-400">
                  No gaps in this category.
                </td>
              </tr>
            ) : (
              activeGaps.map((gap) => (
                <tr
                  key={gap.service_id}
                  className={`hover:bg-amber-50/50 cursor-pointer transition-colors ${
                    gap.tier === 0 ? 'border-l-4 border-l-red-400' :
                    gap.tier === 1 ? 'border-l-4 border-l-orange-400' : ''
                  }`}
                  onClick={() => onNavigate(`registry-detail:${gap.service_id}`)}
                >
                  <td className="px-4 py-3"><TierBadge tier={gap.tier} /></td>
                  <td className="px-4 py-3 font-medium text-gray-900">{gap.name || gap.service_id}</td>
                  <td className="px-4 py-3 text-gray-500">{gap.gap}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
